/**
 * Custom error classes for MCP SDK
 */

/**
 * Base error class for all MCP SDK errors
 */
export class MCPError extends Error {
  constructor(
    message: string,
    public readonly code: string,
    options?: { cause?: unknown }
  ) {
    super(message, options);
    this.name = "MCPError";
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * Authentication error - thrown for 401, token expired, invalid token, etc.
 */
export class MCPAuthError extends MCPError {
  constructor(
    message: string,
    public readonly statusCode?: number,
    options?: { cause?: unknown }
  ) {
    super(message, "AUTH_ERROR", options);
    this.name = "MCPAuthError";
  }
}

/**
 * Type guard to check if an error is an MCPAuthError
 */
export function isMCPAuthError(error: unknown): error is MCPAuthError {
  return error instanceof MCPAuthError;
}

/**
 * Type guard for errors with a numeric code property (like StreamableHTTPError, SseError)
 */
function hasNumericCode(error: unknown): error is Error & { code: number } {
  return (
    error instanceof Error &&
    "code" in error &&
    typeof (error as { code: unknown }).code === "number"
  );
}

function getNumericStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object") {
    return undefined;
  }

  const statusCode =
    "statusCode" in error && typeof error.statusCode === "number"
      ? error.statusCode
      : undefined;
  if (statusCode !== undefined) {
    return statusCode;
  }

  const status =
    "status" in error && typeof error.status === "number"
      ? error.status
      : undefined;
  if (status !== undefined) {
    return status;
  }

  const code =
    "code" in error && typeof error.code === "number"
      ? error.code
      : undefined;
  return code;
}

/**
 * Strictly detects HTTP 401 authorization failures.
 *
 * Unlike isAuthError, this intentionally does not treat 403 or generic
 * auth-looking messages as refreshable. OAuth refresh can repair an expired or
 * rejected access token; it cannot repair insufficient scope.
 */
export function isUnauthorized401(error: unknown): boolean {
  const numericStatus = getNumericStatus(error);
  if (numericStatus !== undefined) {
    return numericStatus === 401;
  }

  if (!(error instanceof Error)) {
    return false;
  }

  if (error.name === "UnauthorizedError") {
    return true;
  }

  const message = error.message.toLowerCase();
  return /\b(?:http|status)[:\s-]*401\b/i.test(message);
}

/**
 * Checks if an error is an authentication-related error.
 * Detects auth errors by:
 * 1. Error class name (UnauthorizedError from MCP SDK)
 * 2. HTTP status codes (401, 403) from transport errors
 * 3. Common auth-related patterns in error messages (case-insensitive)
 */
export function isAuthError(error: unknown): {
  isAuth: boolean;
  statusCode?: number;
} {
  if (!(error instanceof Error)) {
    return { isAuth: false };
  }

  // Check for MCP client's UnauthorizedError by class name
  // (We check by name to avoid importing the runtime class here)
  if (error.name === "UnauthorizedError") {
    return { isAuth: true, statusCode: 401 };
  }

  // Check for our own MCPAuthError by name
  if (error.name === "MCPAuthError") {
    const statusCode =
      "statusCode" in error && typeof error.statusCode === "number"
        ? error.statusCode
        : undefined;
    return { isAuth: true, statusCode };
  }

  // Check for transport errors with HTTP status codes (StreamableHTTPError, SseError)
  if (hasNumericCode(error)) {
    const code = error.code;
    if (code === 401 || code === 403) {
      return { isAuth: true, statusCode: code };
    }
  }

  // Fall back to message pattern matching (case-insensitive)
  const message = error.message.toLowerCase();
  const authPatterns = [
    "unauthorized",
    "invalid_token",
    "invalid token",
    "token expired",
    "token has expired",
    "access denied",
    "authentication failed",
    "authentication required",
    "not authenticated",
    "forbidden",
  ];

  if (authPatterns.some((pattern) => message.includes(pattern))) {
    return { isAuth: true };
  }

  // Check for HTTP status codes in error messages (e.g., "HTTP 401" or "status: 401")
  const statusMatch = message.match(/\b(status[:\s]*)?401\b|\bhttp\s*401\b/i);
  if (statusMatch) {
    return { isAuth: true, statusCode: 401 };
  }

  const forbiddenMatch = message.match(
    /\b(status[:\s]*)?403\b|\bhttp\s*403\b/i
  );
  if (forbiddenMatch) {
    return { isAuth: true, statusCode: 403 };
  }

  return { isAuth: false };
}
